/**
 * Webhook の契約（`docs/CMS_GAP_AND_API.md` §5 が唯一の正）。
 *
 * ここが定義するもの:
 *   - イベント型の登録簿（`WEBHOOK_EVENT_TYPES`）と各 `data` の zod スキーマ
 *   - ペイロードに本文が混入することの**機械的な禁止**
 *   - 署名の計算方法（HMAC-SHA256）と検証
 *   - 再試行スケジュールと再試行可否の判定
 *   - エンドポイント管理 API のリクエスト / レスポンス
 *
 * **`node:crypto` を import しない。** このモジュールは `@mail-sv/shared` の
 * バレル経由で admin / portal のバンドルにも入るため、WebCrypto
 * （`globalThis.crypto.subtle`・Node 22+ とブラウザの双方に存在）を使う。
 * `lib/canonical-json.ts` と同じ理由。
 */
import { z } from 'zod';
import { WEBHOOK_DELIVERY_STATE, WEBHOOK_ENDPOINT_STATUS } from '../enums.js';

/* ======================================================== イベント登録簿 */

/**
 * 発火しうるイベントの全体。**この配列が唯一の出典**で、
 * `webhook_endpoints.event_types` の検証も fan-out 時の照合もここを読む。
 *
 * `webhook.test` だけは性質が違う（`webhook_events` に残さず、
 * `POST /portal/webhooks/:id/test` が同期で 1 通だけ送る）。
 * 購読対象として選べてはいけないので `WEBHOOK_SUBSCRIBABLE_EVENT_TYPES` から外してある。
 */
export const WEBHOOK_EVENT_TYPES = [
  'content.created',
  'content.updated',
  'content.published',
  'content.archived',
  'content.build_failed',
  'content.snippet_outdated',
  'asset.uploaded',
  'asset.replaced',
  'campaign.created',
  'campaign.scheduled',
  'campaign.release_expired',
  'campaign.started',
  'campaign.completed',
  'campaign.paused',
  'campaign.halted',
  'campaign.bounced',
  'contact.subscribed',
  'contact.unsubscribed',
  'contact.complained',
  'contact.bounced',
  'suppression.created',
  'webhook.test',
] as const;

export type WebhookEventType = (typeof WEBHOOK_EVENT_TYPES)[number];

export const webhookEventTypeSchema = z.enum(WEBHOOK_EVENT_TYPES);

/** 契約者が購読対象として選べるイベント。 */
export const WEBHOOK_SUBSCRIBABLE_EVENT_TYPES = WEBHOOK_EVENT_TYPES.filter(
  (t): t is Exclude<WebhookEventType, 'webhook.test'> => t !== 'webhook.test',
);

/**
 * **受信者の個人情報を含むイベント。**
 * 購読するには `webhook_endpoints.pii_ack_at` が必要（`WEBHOOK_PII_ACK_REQUIRED`）。
 *
 * 「メールアドレスが任意の URL へ流れる」ことを契約者に明示的に承諾させる。
 * 承諾の記録が無いまま漏洩すると、我々が経路を作ったことになる。
 */
export const WEBHOOK_PII_EVENT_TYPES: readonly WebhookEventType[] = [
  'contact.subscribed',
  'contact.unsubscribed',
  'contact.complained',
  'contact.bounced',
  'suppression.created',
];

export function isPiiWebhookEvent(type: string): boolean {
  return (WEBHOOK_PII_EVENT_TYPES as readonly string[]).includes(type);
}

/** 購読しようとしているイベント群に PII が含まれるか。 */
export function requiresPiiAck(types: readonly string[]): boolean {
  return types.some(isPiiWebhookEvent);
}

export function isWebhookEventType(value: string): value is WebhookEventType {
  return (WEBHOOK_EVENT_TYPES as readonly string[]).includes(value);
}

/* ------------------------------------------------------------ data の定義 */

const ulid = z.string().min(1).max(64);
const isoDateTime = z.string().datetime({ offset: true });
const sha256Hex = z.string().regex(/^(?:sha256:)?[0-9a-f]{64}$/, 'sha256 は 64 桁の小文字 hex で表します');

const campaignStatsSchema = z
  .object({
    sent: z.number().int().nonnegative(),
    delivered: z.number().int().nonnegative(),
    hardBounced: z.number().int().nonnegative(),
    softBounced: z.number().int().nonnegative(),
    complained: z.number().int().nonnegative(),
    unsubscribed: z.number().int().nonnegative(),
  })
  .strict();

/**
 * イベント種別ごとの `data`。
 *
 * **`bodyHtml` / `docJson` の類は 1 つも無い。** 本文由来の文字列を外部へ出さないのが
 * 本設計の中核制約で、`subject` だけは `content_documents.subject`（DB の列）なので許す。
 */
export const WEBHOOK_EVENT_DATA_SCHEMAS = {
  'content.created': z
    .object({
      documentId: ulid,
      kind: z.string(),
      name: z.string(),
      subject: z.string().nullable().optional(),
      tags: z.array(z.string()).default([]),
      origin: z.string(),
      externalRef: z.string().nullable().optional(),
    })
    .strict(),
  'content.updated': z
    .object({
      documentId: ulid,
      revisionId: ulid,
      revision: z.number().int().positive(),
      docFormat: z.string(),
      contentHash: sha256Hex,
      byteSize: z.number().int().nonnegative(),
    })
    .strict(),
  'content.published': z
    .object({
      documentId: ulid,
      revisionId: ulid,
      revision: z.number().int().positive(),
      approvedAt: isoDateTime,
      /** 予約承認（`content_schedules`）由来か、人が押したか。 */
      scheduledPromotion: z.boolean(),
    })
    .strict(),
  'content.archived': z.object({ documentId: ulid, archivedAt: isoDateTime }).strict(),
  'content.build_failed': z
    .object({
      documentId: ulid,
      revisionId: ulid,
      /**
       * **`errorMessage` は返さない。** レンダラの例外メッセージには
       * 本文の断片（壊れたタグの周辺など）が混ざる。symbol だけを出す。
       */
      errorCode: z.string(),
    })
    .strict(),
  'content.snippet_outdated': z
    .object({
      documentId: ulid,
      snippetDocumentId: ulid,
      usedSnippetRevision: z.number().int().positive(),
      latestSnippetRevision: z.number().int().positive(),
    })
    .strict(),
  'asset.uploaded': z
    .object({
      assetId: ulid,
      fileName: z.string(),
      contentType: z.string(),
      bytes: z.number().int().nonnegative(),
      width: z.number().int().positive().nullable().optional(),
      height: z.number().int().positive().nullable().optional(),
      publicUrl: z.string().url(),
    })
    .strict(),
  'asset.replaced': z
    .object({
      assetId: ulid,
      stableUrl: z.string().url(),
      previousSha256: sha256Hex,
      newSha256: sha256Hex,
    })
    .strict(),
  'campaign.created': z
    .object({
      campaignId: ulid,
      name: z.string(),
      listId: ulid,
      contentDocumentId: ulid.nullable().optional(),
      externalRef: z.string().nullable().optional(),
    })
    .strict(),
  'campaign.scheduled': z
    .object({ campaignId: ulid, scheduledAt: isoDateTime, releasedAt: isoDateTime })
    .strict(),
  'campaign.release_expired': z.object({ campaignId: ulid, scheduledAt: isoDateTime }).strict(),
  'campaign.started': z
    .object({ campaignId: ulid, startedAt: isoDateTime, recipientCount: z.number().int().nonnegative() })
    .strict(),
  'campaign.completed': z
    .object({ campaignId: ulid, completedAt: isoDateTime, stats: campaignStatsSchema })
    .strict(),
  'campaign.paused': z
    .object({ campaignId: ulid, reason: z.string(), pausedBy: z.string().nullable().optional() })
    .strict(),
  'campaign.halted': z.object({ campaignId: ulid, reason: z.string() }).strict(),
  /**
   * **1 件ごとのバウンスではなく、キャンペーン単位の閾値超過アラート**（§5.3）。
   * 受信者単位で飛ばすと、10 万通の配信で契約者のエンドポイントを我々が DDoS することになる。
   */
  'campaign.bounced': z
    .object({
      campaignId: ulid,
      window: z.string(),
      evaluatedAt: isoDateTime,
      hardBounceRate: z.number().min(0).max(1),
      threshold: z.number().min(0).max(1),
      hardBounces: z.number().int().nonnegative(),
      delivered: z.number().int().nonnegative(),
    })
    .strict(),
  'contact.subscribed': z
    .object({
      contactId: ulid,
      listId: ulid,
      email: z.string(),
      optinMethod: z.string(),
      occurredAt: isoDateTime,
    })
    .strict(),
  'contact.unsubscribed': z
    .object({
      contactId: ulid,
      listId: ulid.nullable().optional(),
      email: z.string(),
      method: z.string(),
      campaignId: ulid.nullable().optional(),
      occurredAt: isoDateTime,
    })
    .strict(),
  'contact.complained': z
    .object({
      contactId: ulid,
      email: z.string(),
      campaignId: ulid.nullable().optional(),
      occurredAt: isoDateTime,
    })
    .strict(),
  'contact.bounced': z
    .object({
      contactId: ulid,
      email: z.string(),
      dsnStatus: z.string(),
      campaignId: ulid.nullable().optional(),
      occurredAt: isoDateTime,
    })
    .strict(),
  'suppression.created': z
    .object({
      email: z.string(),
      reason: z.string(),
      scope: z.string(),
      sourceCampaignId: ulid.nullable().optional(),
      expiresAt: isoDateTime.nullable().optional(),
    })
    .strict(),
  /** 疎通確認専用。固定ペイロードで、`webhook_events` には残らない。 */
  'webhook.test': z.object({ endpointId: ulid, message: z.string() }).strict(),
} as const satisfies Record<WebhookEventType, z.ZodTypeAny>;

export type WebhookEventDataMap = {
  [K in WebhookEventType]: z.infer<(typeof WEBHOOK_EVENT_DATA_SCHEMAS)[K]>;
};

/* ------------------------------------------------------------- ペイロード */

export const WEBHOOK_API_VERSION = 'v1';

export const webhookPayloadSchema = z
  .object({
    /** `webhook_events.id`（ULID）。**同一イベントの再試行では変わらない**。 */
    id: ulid,
    type: webhookEventTypeSchema,
    apiVersion: z.string(),
    occurredAt: isoDateTime,
    organizationId: ulid,
    data: z.record(z.unknown()),
  })
  .strict();

export type WebhookPayload = z.infer<typeof webhookPayloadSchema>;

/** 型付きのペイロード。`emitWebhookEvent()` の引数はこの形で受ける。 */
export interface TypedWebhookPayload<T extends WebhookEventType = WebhookEventType> {
  readonly id: string;
  readonly type: T;
  readonly apiVersion: string;
  readonly occurredAt: string;
  readonly organizationId: string;
  readonly data: WebhookEventDataMap[T];
}

/**
 * ペイロードに入っていてはならないキー。
 *
 * `apps/api/src/services/audit.ts` の `FORBIDDEN_KEY_RE` と同じ語彙に本文系を足したもの。
 * **監査は事故っても記録が残ればよいが、Webhook は外部へ出る。**
 * 気づかず出すほうが害が大きいので、redact ではなく throw する。
 */
export const WEBHOOK_PAYLOAD_FORBIDDEN_KEY_RE =
  /(password|secret|token|apiKey|api_key|privateKey|private_key|credential|authorization|cookie|whsec|bodyHtml|body_html|bodyText|body_text|docJson|doc_json|rawHtml|raw_html|htmlSource|previewHtml|snippetJson|mjml|prosemirror)/i;

export class WebhookPayloadForbiddenKeyError extends Error {
  readonly path: string;
  constructor(path: string) {
    super(`Webhook ペイロードに出してはならないキーが含まれています: ${path}`);
    this.name = 'WebhookPayloadForbiddenKeyError';
    this.path = path;
  }
}

/**
 * ペイロードを再帰的に走査し、禁止キーがあれば**例外を投げる**。
 * `emitWebhookEvent()` の入口で必ず呼ぶこと。開発時に必ず落ちる形にするのが狙い。
 */
export function assertWebhookPayloadClean(value: unknown, path = '$'): void {
  if (value === null || typeof value !== 'object') return;
  if (Array.isArray(value)) {
    value.forEach((v, i) => assertWebhookPayloadClean(v, `${path}[${i}]`));
    return;
  }
  for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
    if (WEBHOOK_PAYLOAD_FORBIDDEN_KEY_RE.test(key)) {
      throw new WebhookPayloadForbiddenKeyError(`${path}.${key}`);
    }
    assertWebhookPayloadClean(child, `${path}.${key}`);
  }
}

/** イベントを組み立てる。型と禁止キーの両方をここで担保する。 */
export function buildWebhookPayload<T extends WebhookEventType>(params: {
  id: string;
  type: T;
  organizationId: string;
  occurredAt: Date | string;
  data: WebhookEventDataMap[T];
}): TypedWebhookPayload<T> {
  const payload: TypedWebhookPayload<T> = {
    id: params.id,
    type: params.type,
    apiVersion: WEBHOOK_API_VERSION,
    occurredAt: typeof params.occurredAt === 'string' ? params.occurredAt : params.occurredAt.toISOString(),
    organizationId: params.organizationId,
    data: params.data,
  };
  assertWebhookPayloadClean(payload.data, '$.data');
  return payload;
}

/* ================================================================== 署名 */

export const WEBHOOK_HEADERS = {
  eventId: 'X-MailSV-Event-Id',
  eventType: 'X-MailSV-Event-Type',
  deliveryId: 'X-MailSV-Delivery-Id',
  attempt: 'X-MailSV-Attempt',
  signature: 'X-MailSV-Signature',
} as const;

export const WEBHOOK_USER_AGENT = 'MailSV-Webhook/1.0';

/** 秘密の接頭辞。**署名鍵はこれを除いた部分の UTF-8 バイト**。 */
export const WEBHOOK_SECRET_PREFIX = 'whsec_';

/** 受信側に推奨するタイムスタンプ許容差（秒）。再試行のたびに `t` は計算し直される。 */
export const WEBHOOK_SIGNATURE_TOLERANCE_SEC = 300;

/**
 * 署名対象の文字列を組み立てる。
 *
 *   `${t}.${rawBody}`
 *
 * **`rawBody` は送信した生バイト列そのもの**で、JSON を再シリアライズしない。
 * 再シリアライズするとキー順や空白の差で受信側の検証が落ちる
 * （実装した相手が最初に踏む地雷なので、ここを 1 か所に固定する）。
 */
export function webhookSignatureBase(timestampSec: number, rawBody: string): string {
  return `${timestampSec}.${rawBody}`;
}

/** `whsec_` を取り除いた鍵素材。付いていない文字列はそのまま鍵として扱う。 */
export function webhookSecretMaterial(secret: string): string {
  return secret.startsWith(WEBHOOK_SECRET_PREFIX) ? secret.slice(WEBHOOK_SECRET_PREFIX.length) : secret;
}

async function hmacSha256Hex(keyMaterial: string, message: string): Promise<string> {
  const encoder = new TextEncoder();
  const key = await globalThis.crypto.subtle.importKey(
    'raw',
    encoder.encode(keyMaterial),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const sig = await globalThis.crypto.subtle.sign('HMAC', key, encoder.encode(message));
  return Array.from(new Uint8Array(sig), (b) => b.toString(16).padStart(2, '0')).join('');
}

/** 1 つの秘密で署名を計算する（小文字 hex）。 */
export async function signWebhookPayload(
  secret: string,
  timestampSec: number,
  rawBody: string,
): Promise<string> {
  return hmacSha256Hex(webhookSecretMaterial(secret), webhookSignatureBase(timestampSec, rawBody));
}

/**
 * `X-MailSV-Signature` ヘッダを組み立てる。
 *
 * 秘密のローテーション中は**有効な全世代で署名する**ので `v1=` が複数現れる。
 * 受信側は「どれか 1 つが一致すれば正」と実装する。
 */
export function formatWebhookSignatureHeader(timestampSec: number, signatures: readonly string[]): string {
  return [`t=${timestampSec}`, ...signatures.map((s) => `v1=${s}`)].join(',');
}

/** 全世代の秘密で署名してヘッダ文字列まで作る。 */
export async function buildWebhookSignatureHeader(
  secrets: readonly string[],
  rawBody: string,
  timestampSec: number = Math.floor(Date.now() / 1000),
): Promise<string> {
  const signatures = await Promise.all(secrets.map((s) => signWebhookPayload(s, timestampSec, rawBody)));
  return formatWebhookSignatureHeader(timestampSec, signatures);
}

export interface ParsedWebhookSignature {
  readonly timestampSec: number;
  readonly signatures: string[];
}

export function parseWebhookSignatureHeader(header: string): ParsedWebhookSignature | null {
  let timestampSec: number | null = null;
  const signatures: string[] = [];
  for (const part of header.split(',')) {
    const eq = part.indexOf('=');
    if (eq < 0) continue;
    const key = part.slice(0, eq).trim();
    const value = part.slice(eq + 1).trim();
    if (key === 't') {
      if (!/^\d+$/.test(value)) return null;
      timestampSec = Number(value);
    } else if (key === 'v1') {
      if (/^[0-9a-f]{64}$/.test(value)) signatures.push(value);
    }
  }
  if (timestampSec === null || signatures.length === 0) return null;
  return { timestampSec, signatures };
}

/**
 * 長さに依存しない比較。
 *
 * `crypto.timingSafeEqual` はブラウザに無いので、hex 文字列同士の
 * 定数時間比較を自前で書く。**早期 return しない**のが要点。
 */
export function timingSafeEqualHex(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i += 1) {
    diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }
  return diff === 0;
}

export interface VerifyWebhookSignatureParams {
  readonly header: string;
  readonly rawBody: string;
  /** 有効な秘密（ローテーション中は複数）。 */
  readonly secrets: readonly string[];
  readonly toleranceSec?: number;
  readonly now?: Date;
}

export type WebhookVerifyResult =
  | { ok: true; timestampSec: number }
  | { ok: false; reason: 'MALFORMED_HEADER' | 'TIMESTAMP_OUT_OF_TOLERANCE' | 'SIGNATURE_MISMATCH' };

/**
 * 受信側の検証手順の**参照実装**。契約者向けドキュメントにこの手順をそのまま載せる。
 * `POST /portal/webhooks/:id/test` の自己検証にも使う。
 */
export async function verifyWebhookSignature(params: VerifyWebhookSignatureParams): Promise<WebhookVerifyResult> {
  const parsed = parseWebhookSignatureHeader(params.header);
  if (!parsed) return { ok: false, reason: 'MALFORMED_HEADER' };

  const tolerance = params.toleranceSec ?? WEBHOOK_SIGNATURE_TOLERANCE_SEC;
  const nowSec = Math.floor((params.now?.getTime() ?? Date.now()) / 1000);
  if (Math.abs(nowSec - parsed.timestampSec) > tolerance) {
    return { ok: false, reason: 'TIMESTAMP_OUT_OF_TOLERANCE' };
  }

  const base = webhookSignatureBase(parsed.timestampSec, params.rawBody);
  for (const secret of params.secrets) {
    const expected = await hmacSha256Hex(webhookSecretMaterial(secret), base);
    // 一致しても break しない。どの秘密で通ったかを実行時間から推測させない。
    if (parsed.signatures.some((sig) => timingSafeEqualHex(sig, expected))) {
      return { ok: true, timestampSec: parsed.timestampSec };
    }
  }
  return { ok: false, reason: 'SIGNATURE_MISMATCH' };
}

/* ========================================================== 再試行の規約 */

/**
 * 試行 n 回目を出すまでの遅延（前の試行から）。
 *
 * **BullMQ の `attempts` / `backoff` に任せない。**
 * ① 再試行の状況を契約者に見せる画面が要り、Redis の中は UI から見せられない。
 * ② Redis が飛んでも DB に残った `pending` を sweep から拾い直せる。
 */
export const WEBHOOK_RETRY_DELAYS_MS: readonly number[] = [
  0, // 1 回目
  30_000, // 2 回目（30 秒後）
  120_000, // 3 回目（2 分後）
  600_000, // 4 回目（10 分後）
  3_600_000, // 5 回目（1 時間後）
  21_600_000, // 6 回目（6 時間後）
];

export const WEBHOOK_MAX_ATTEMPTS_DEFAULT = WEBHOOK_RETRY_DELAYS_MS.length;

/** `Retry-After` を尊重するときの上限。6 時間より先は待たない。 */
export const WEBHOOK_RETRY_AFTER_MAX_MS = 21_600_000;

/**
 * 次の試行までの遅延（ミリ秒）。`attempt` は**これまでに行った試行回数**。
 * 上限に達していれば null（`state='exhausted'`）。
 */
export function webhookNextAttemptDelayMs(
  attempt: number,
  maxAttempts: number = WEBHOOK_MAX_ATTEMPTS_DEFAULT,
): number | null {
  if (attempt >= maxAttempts) return null;
  return WEBHOOK_RETRY_DELAYS_MS[attempt] ?? WEBHOOK_RETRY_DELAYS_MS[WEBHOOK_RETRY_DELAYS_MS.length - 1] ?? null;
}

export type WebhookAttemptOutcome =
  | { kind: 'success' }
  /** 再試行する。 */
  | { kind: 'retry'; retryAfterMs: number | null }
  /** 再試行しない（恒久的な失敗）。 */
  | { kind: 'permanent' }
  /** 相手が「もう要らない」と言っている。エンドポイントを paused にする。 */
  | { kind: 'gone' };

/**
 * HTTP ステータスから次にどうするかを決める。
 *
 * 再試行する: 接続失敗 / タイムアウト / 5xx / 408 / 429。
 * 再試行しない: それ以外の 4xx（相手の設定が間違っているので、待っても直らない）。
 */
export function classifyWebhookResponse(status: number, retryAfterHeader?: string | null): WebhookAttemptOutcome {
  if (status >= 200 && status < 300) return { kind: 'success' };
  if (status === 410) return { kind: 'gone' };
  if (status === 408 || status === 429 || status >= 500) {
    return { kind: 'retry', retryAfterMs: parseRetryAfterMs(retryAfterHeader) };
  }
  return { kind: 'permanent' };
}

/** `Retry-After`（秒数 or HTTP-date）をミリ秒に直す。上限で丸める。 */
export function parseRetryAfterMs(header: string | null | undefined, now: Date = new Date()): number | null {
  if (!header) return null;
  const trimmed = header.trim();
  if (/^\d+$/.test(trimmed)) {
    return Math.min(Number(trimmed) * 1000, WEBHOOK_RETRY_AFTER_MAX_MS);
  }
  const at = Date.parse(trimmed);
  if (Number.isNaN(at)) return null;
  const delta = at - now.getTime();
  if (delta <= 0) return 0;
  return Math.min(delta, WEBHOOK_RETRY_AFTER_MAX_MS);
}

export interface WebhookAutoDisableInput {
  readonly consecutiveFailures: number;
  readonly lastSuccessAt: Date | null;
  readonly createdAt: Date;
  readonly failureThreshold: number;
  readonly staleHours: number;
  readonly now?: Date;
}

/**
 * 自動無効化の判定。**AND 条件**であることが要点。
 *
 * 連続失敗数だけを見ると、低頻度のエンドポイントが数件の失敗で死ぬ。
 * 経過時間だけを見ると、高頻度のエンドポイントが 3 日間失敗し続けても止まらない。
 */
export function shouldAutoDisableWebhook(input: WebhookAutoDisableInput): boolean {
  if (input.consecutiveFailures < input.failureThreshold) return false;
  const now = input.now ?? new Date();
  // 一度も成功していないエンドポイントは作成時刻を起点にする。
  const since = input.lastSuccessAt ?? input.createdAt;
  return now.getTime() - since.getTime() >= input.staleHours * 60 * 60 * 1000;
}

/** `response_snippet` に入れてよい長さ（先頭 512 バイト）。 */
export const WEBHOOK_RESPONSE_SNIPPET_BYTES = 512;
/** レスポンスを読む上限（読み捨てる）。 */
export const WEBHOOK_RESPONSE_READ_BYTES = 8 * 1024;

/** レスポンス本文を `response_snippet` 用に切り詰める。 */
export function truncateResponseSnippet(body: string, maxBytes = WEBHOOK_RESPONSE_SNIPPET_BYTES): string {
  const bytes = new TextEncoder().encode(body);
  if (bytes.length <= maxBytes) return body;
  return new TextDecoder('utf-8', { fatal: false }).decode(bytes.slice(0, maxBytes)).replace(/�+$/, '');
}

/* ================================================== エンドポイント管理 API */

export const webhookEndpointUrlSchema = z
  .string()
  .url()
  .max(2048)
  .refine((v) => v.startsWith('https://') || v.startsWith('http://'), {
    message: 'URL は https:// で始めてください',
  });

export const createWebhookEndpointSchema = z
  .object({
    name: z.string().min(1).max(100),
    url: webhookEndpointUrlSchema,
    eventTypes: z.array(webhookEventTypeSchema).min(1).max(WEBHOOK_EVENT_TYPES.length),
    description: z.string().max(500).optional(),
    /** PII を含むイベントを購読するときに必要な明示的な承諾。 */
    piiAck: z.boolean().optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.eventTypes.includes('webhook.test')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'webhook.test は購読できません（疎通確認専用です）',
        path: ['eventTypes'],
      });
    }
    if (requiresPiiAck(value.eventTypes) && value.piiAck !== true) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '受信者の個人情報を含むイベントを購読するには、送信先での取り扱いへの同意が必要です',
        path: ['piiAck'],
      });
    }
  });

export type CreateWebhookEndpointInput = z.infer<typeof createWebhookEndpointSchema>;

export const updateWebhookEndpointSchema = z
  .object({
    name: z.string().min(1).max(100).optional(),
    url: webhookEndpointUrlSchema.optional(),
    eventTypes: z.array(webhookEventTypeSchema).min(1).optional(),
    description: z.string().max(500).optional(),
    status: z.enum(['active', 'paused']).optional(),
    piiAck: z.boolean().optional(),
  })
  .strict();

export const rotateWebhookSecretSchema = z
  .object({
    /** 旧秘密を併用する期間。この間は両方の秘密で署名する。 */
    overlapHours: z.number().int().min(0).max(168).default(24),
  })
  .strict();

export const webhookEndpointSchema = z
  .object({
    id: ulid,
    name: z.string(),
    url: z.string(),
    eventTypes: z.array(z.string()),
    apiVersion: z.string(),
    status: z.enum(WEBHOOK_ENDPOINT_STATUS),
    description: z.string().nullable(),
    piiAckAt: isoDateTime.nullable(),
    secretVersion: z.number().int().positive(),
    /** 表示用の接頭辞のみ。**生の秘密は作成・ローテーションの応答にしか出ない**。 */
    secretPrefix: z.string(),
    consecutiveFailures: z.number().int().nonnegative(),
    lastSuccessAt: isoDateTime.nullable(),
    lastFailureAt: isoDateTime.nullable(),
    lastErrorMessage: z.string().nullable(),
    disabledReason: z.string().nullable(),
    createdAt: isoDateTime,
    updatedAt: isoDateTime,
  })
  .strict();

export type WebhookEndpointView = z.infer<typeof webhookEndpointSchema>;

/** 作成 / ローテーションの応答。`secret` はこの 1 回しか返らない。 */
export const webhookEndpointWithSecretSchema = webhookEndpointSchema.extend({
  secret: z.string().startsWith(WEBHOOK_SECRET_PREFIX),
});

export const webhookDeliverySchema = z
  .object({
    id: ulid,
    endpointId: ulid,
    eventId: ulid,
    eventType: z.string(),
    attempt: z.number().int().nonnegative(),
    state: z.enum(WEBHOOK_DELIVERY_STATE),
    httpStatus: z.number().int().nullable(),
    responseSnippet: z.string().nullable(),
    errorMessage: z.string().nullable(),
    requestDurationMs: z.number().int().nullable(),
    nextAttemptAt: isoDateTime.nullable(),
    deliveredAt: isoDateTime.nullable(),
    createdAt: isoDateTime,
  })
  .strict();

/** `POST /portal/webhooks/:id/test` の応答。**同期で返す**（切り分けができないと詰む）。 */
export const webhookTestResultSchema = z
  .object({
    httpStatus: z.number().int().nullable(),
    durationMs: z.number().int().nonnegative(),
    error: z.string().nullable(),
    signatureHeader: z.string(),
  })
  .strict();

/** ポーリング面（`GET /api/v1/content/events`）のクエリ。 */
export const webhookEventPollQuerySchema = z
  .object({
    /** 最後に処理したイベント ID。クライアントが保存する（opaque カーソルとは別物）。 */
    after: ulid.optional(),
    cursor: z.string().max(500).optional(),
    /** PII を含む type を取るときは明示指定を必須にする。 */
    types: z.string().max(2000).optional(),
    limit: z.number().int().min(1).max(200).default(100),
  })
  .strict();

export const webhookEventItemSchema = z
  .object({
    id: ulid,
    type: z.string(),
    occurredAt: isoDateTime,
    resourceType: z.string().nullable(),
    resourceId: z.string().nullable(),
    data: z.record(z.unknown()),
  })
  .strict();
